/**
 * Centralized cache invalidations for the app's central resources.
 *
 * The central resources are the entities that appear across many features
 * of the beneficiary-facing app (user profile, beneficiaries, cards, bons
 * d'achat, rechargements, remboursements...). Each resource has more than
 * one query-key namespace in the codebase. Centralizing the invalidation
 * logic here avoids drift between mutations and missed invalidations.
 *
 * Usage:
 *   - In a mutation, declare `meta: { invalidateResources: ["vouchers"] }`
 *     and the global mutation handler in query-client.ts will run the
 *     invalidations on success.
 *   - For ad-hoc usage (components, success callbacks of imperative jobs)
 *     call `invalidateCentralResources(queryClient, ["vouchers"])`.
 */
import type { QueryClient, QueryKey } from "@tanstack/react-query";

export const CENTRAL_RESOURCES = [
  "user",
  "organization",
  "plan",
  "beneficiaries",
  "cards",
  "vouchers",
  "top-ups",
  "refunds",
] as const;

export type CentralResource = (typeof CENTRAL_RESOURCES)[number];

/**
 * For each central resource, list every distinct query-key namespace
 * (i.e. distinct cache root) that must be invalidated when the resource
 * is mutated. Each entry uses prefix matching (exact: false).
 *
 * Notes on inconsistencies kept for backwards compatibility:
 *  - vouchers: both ["voucherEmployees"] (vouchers feature) and
 *    ["voucher-employees"] (dashboard widget) are real, distinct caches.
 *    Both must be invalidated together.
 *  - expenses live under ["expenses"], ["recent-expenses"] and
 *    ["grouped-expenses"]: they all reflect voucher balance changes.
 */
const RESOURCE_QUERY_KEYS: Record<CentralResource, readonly QueryKey[]> = {
  "user": [
    ["user"],
    ["userMandatoryFields"],
    // The sidebar is served by the backend, which hides the entries the
    // employee cannot reach: lotteries, polls, surveys and the advent calendar
    // follow his effective groups, dynamic groups included, themselves filtered
    // on custom attributes he can change from his own profile. A toggle that
    // left this cache alone would keep an entry leading to a refusal, or hide
    // one he just earned.
    ["brand-theme"],
  ],
  "organization": [
    ["organization"],
    ["cmsPages"],
  ],
  "plan": [
    ["plan"],
  ],
  "beneficiaries": [
    ["beneficiaries"],
  ],
  "cards": [
    ["cards"],
    ["pinCard"],
  ],
  "vouchers": [
    ["voucherEmployees"],
    ["voucher-employees"],
    ["expenses"],
    ["recent-expenses"],
    ["grouped-expenses"],
  ],
  "top-ups": [
    ["topups"],
  ],
  "refunds": [
    ["refund-requests"],
  ],
};

/**
 * Invalidate every cache namespace associated with the given central
 * resources. Each query key is invalidated via prefix matching, and
 * duplicates across resources are deduplicated.
 */
export const invalidateCentralResources = (
  queryClient: QueryClient,
  resources: readonly CentralResource[],
): void => {
  const seen = new Set<string>();
  for (const resource of resources) {
    for (const queryKey of RESOURCE_QUERY_KEYS[resource]) {
      const signature = JSON.stringify(queryKey);
      if (seen.has(signature)) continue;
      seen.add(signature);
      queryClient.invalidateQueries({ queryKey, exact: false });
    }
  }
};

/**
 * Resources to invalidate when the user's voucher balance changes
 * (top-up, refund creation, checkout payment). Hits the vouchers list,
 * the expenses caches, and the top-up / refund histories that are
 * derived from those movements.
 */
export const BALANCE_CHANGE_RESOURCES = [
  "vouchers",
  "top-ups",
  "refunds",
] as const satisfies readonly CentralResource[];
